<script setup lang="ts">
import { store } from '@/store/store';
import { useUIStore } from '@/store/ui-store';
import { suppressKeyboardEvent } from './focus';

const uiStore = useUIStore();
</script>

<template>
  <div style="display: grid; grid-template-rows: 1fr; flex-grow: 1">
    <slot
      v-bind="{
        class: uiStore.isDarkMode ? 'ag-theme-material-dark' : 'ag-theme-material',
        headerHeight: 32,
        overlayNoRowsTemplate: 'No data available',
        rowHeight: 32,
        rowSelection: 'multiple',
        sortingOrder: ['asc', 'desc'],
        suppressRowVirtualisation: false,
        suppressColumnVirtualisation: store.state.clientConfig?.suppressGridVirtualisation ?? false,
        suppressMaxRenderedRowRestriction: false,
        suppressRowClickSelection: true,
        defaultColDef: { suppressKeyboardEvent },
        detailRowHeight: true,
      }"
    />
  </div>
</template>

<style lang="scss">
.ag-center-cols-container {
  min-width: 100%;
}

:is(.ag-theme-material, .ag-theme-material-dark, .ag-theme-material-auto-dark)
  .ag-ltr
  :is(
    input[class^='ag-']:not([type]),
    input[class^='ag-'][type='text'],
    input[class^='ag-'][type='number'],
    input[class^='ag-'][type='tel'],
    input[class^='ag-'][type='date'],
    input[class^='ag-'][type='datetime-local'],
    textarea[class^='ag-'],
    select[class^='ag-'],
    .ag-input-field-input
  ) {
  --ag-grid-size: 4px;
  height: calc(var(--ag-grid-size) * 5);
  border: var(--ag-borders-input) var(--ag-input-border-color) !important;
  padding: 0 4px !important;
  &:focus {
    outline: none;
    box-shadow: var(--ag-input-focus-box-shadow);
    border-color: var(--ag-input-focus-border-color);
  }
}

.theme--dark .ag-theme-material-dark {
  --ag-header-foreground-color: rgba(255, 255, 255, 0.7) !important;
  --ag-header-cell-hover-background-color: transparent;
  --ag-header-background-color: transparent;
  --ag-footer-background-color: transparent;
  --ag-background-color: #1e1e1e;
  --ag-odd-row-background-color: transparent;
  --ag-checkbox-checked-color: #fff;
  --ag-checkbox-unchecked-color: #fff;
  --ag-checkbox-background-color: transparent;
  --ag-material-accent-color: #fff;
  --ag-range-selection-border-color: #fff;
  --ag-selected-row-background-color: #505050;
  --ag-row-hover-color: #616161;
  --ag-cell-horizontal-padding: 1rem;
  --ag-value-change-value-highlight-background-color: #888;
  --scrollbarBG: #282828;
  --ag-borders: solid 1px;
  --ag-borders-secondary: var(--ag-borders);
  --ag-borders-input: var(--ag-borders-secondary);
  --ag-input-border-color: #aaa;
  --ag-input-focus-border-color: #719ece;
  --ag-input-focus-box-shadow: 0 0 4px 1.5px var(--ag-input-focus-border-color);
  --ag-cell-horizontal-padding: 0.5rem !important;

  .ag-floating-bottom {
    background-color: #141414;
  }
}

.ag-header-cell {
  /* ignore checkbox header */
  &:not(:has(.ag-header-select-all:not(.ag-hidden))) {
    --ag-cell-horizontal-padding: 0.5rem !important;
  }

  .ag-cell-label-container {
    flex-direction: row;

    .ag-sort-indicator-container {
      /**
           * hide container for sorting indicator that has no visible children
           * in order to exclude it from flex flow and remove useless gap
           */
      &:not(:has(.ag-sort-indicator-icon:not(.ag-hidden))) {
        display: none;
      }

      & > * {
        padding: 0;
      }
    }

    &.ag-header-cell-sorted-none:not(:hover) {
      opacity: 0.7;
    }

    .ag-header-cell-label {
      gap: 0.5rem;
      padding-left: 0.5rem;
    }

    &.header-center .ag-header-cell-label {
      justify-content: center;
      justify-content: flex-center;

      .ag-sort-indicator-container:not(:has(> :not(.ag-hidden))) {
        display: block !important;
        .ag-hidden.ag-sort-none-icon {
          display: block !important;
          visibility: hidden;
        }
      }
    }

    &.header-right .ag-header-cell-label {
      justify-content: end;
      justify-content: flex-end;
    }
  }

  &.header-center .ag-header-cell-label {
    justify-content: flex-center;
  }

  &.header-right .ag-header-cell-label {
    justify-content: flex-end;
  }
}

.ag-cell {
  display: flex;
  justify-content: start;
  align-items: center;

  &:has(
    /** cell has focusable elements, don't allow to focus */
    :is(
      button,
      a[href],
      input:not([type='hidden']),
      select,
      textarea,
      [tabindex]
    ):not(
      [disabled],
      [tabindex^='-'] /** eliminate negative values */
    )
  ) {
    &:focus-within {
      border-color: transparent;
    }
  }

  &.row-drag-cell {
    padding: 0 !important;
    justify-content: center;
    align-items: center;
    line-height: 1;

    .ag-drag-handle {
      margin: 0;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
    }

    .ag-icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}

.v-text-field {
  margin: 0;
}

// Make the drag ghost invisible while keeping drag functionality
.ag-dnd-ghost {
  opacity: 0 !important;
  pointer-events: none !important;
}
</style>
